import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import agentApiService from '../../services/agentApiService';
import { toast } from 'react-toastify';

const CancellationDetail = () => {
  const { cancellationId } = useParams();
  const navigate = useNavigate();
  const [cancellationDetail, setCancellationDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    queryType: 'CANCELLATION',
    query: ''
  });

  useEffect(() => {
    if (cancellationId) {
      fetchCancellationDetail();
    }
  }, [cancellationId]);

  const fetchCancellationDetail = async () => {
    try {
      setLoading(true);
      const response = await agentApiService.getCancellationDetail(cancellationId);
      
      if (response.success) {
        setCancellationDetail(response.cancellationDetail);
      } else {
        setError(response.message || 'Failed to fetch cancellation details');
        toast.error(response.message || 'Failed to fetch cancellation details');
      }
    } catch (error) {
      console.error('Error fetching cancellation detail:', error);
      setError(error.message);
      toast.error('Failed to fetch cancellation details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await agentApiService.createSupportTicket(cancellationDetail.bookingId, ticketForm);
      if (response.success) {
        toast.success('Support ticket submitted successfully');
        setShowTicketModal(false);
        setTicketForm({ queryType: 'CANCELLATION', query: '' });
      }
    } catch (error) {
      toast.error('Failed to submit ticket: ' + error.message);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'CANCELLED':
      case 'AUTO_CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Cancellation Details</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/agent/dashboard/booking-cancellation')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mr-2"
          >
            Back to Cancellations
          </button>
          <button
            onClick={fetchCancellationDetail}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!cancellationDetail) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-xl mb-4">📋 Cancellation Not Found</div>
          <button
            onClick={() => navigate('/agent/dashboard/booking-cancellation')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Cancellations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/agent/dashboard/booking-cancellation')}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            ← Back to Cancellations
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Cancellation Details</h1>
          <p className="text-gray-600">Cancellation ID: {cancellationId}</p>
        </div>
        <button
          onClick={() => setShowTicketModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          🎫 Add Ticket
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Cancellation Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cancellation Information Card */}
          <div className="bg-white rounded-lg shadow border">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                🚫 Cancellation Information
              </h2>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(cancellationDetail.status)}`}>
                {cancellationDetail.status}
              </span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Cancellation ID</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.cancellationId}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Booking ID</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.bookingId}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Hotel Name</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.hotelName || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">City</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.city || 'N/A'}</div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Customer Name</label>
                    <div className="text-lg font-semibold text-gray-900">
                      {`${cancellationDetail.passengerFirstName || ''} ${cancellationDetail.passengerLastName || ''}`.trim() || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Customer Email</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.passengerEmail || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Phone Number</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.phoneNumber || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Confirmation Number</label>
                    <div className="text-lg font-semibold text-gray-900">{cancellationDetail.confirmationNo || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Dates Card */}
          <div className="bg-white rounded-lg shadow border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                📅 Booking Dates
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Check-in Date</label>
                    <div className="text-lg font-semibold text-gray-900">{formatDate(cancellationDetail.checkInDate)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Booking Date</label>
                    <div className="text-lg font-semibold text-gray-900">{formatDate(cancellationDetail.bookingDate)}</div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Check-out Date</label>
                    <div className="text-lg font-semibold text-gray-900">{formatDate(cancellationDetail.checkOutDate)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Cancellation Date</label>
                    <div className="text-lg font-semibold text-gray-900">{formatDate(cancellationDetail.cancellationDate)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Refund Summary and Policy */}
        <div className="space-y-6">
          {/* Refund Summary Card */}
          <div className="bg-white rounded-lg shadow border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                💰 Refund Summary
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-700">Room Price:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(cancellationDetail.roomPrice)}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium text-gray-700">Total Refund Price:</span>
                    <span className="text-xl font-bold text-green-600">
                      {formatCurrency(cancellationDetail.totalPrice)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Policy Card */}
          <div className="bg-white rounded-lg shadow border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                📋 Refund Policy
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-red-800 font-semibold">
                  📋 Refund Terms
                </div>
                
                <div className="space-y-3">
                  <div className="bg-white rounded p-3 border-l-4 border-red-500">
                    <div className="font-semibold text-gray-900 mb-1">Processing Time</div>
                    <div className="text-sm text-gray-600">
                      Refund will be processed within 7-10 business days from the date of cancellation confirmation.
                    </div>
                  </div>
                  
                  <div className="bg-white rounded p-3 border-l-4 border-red-500">
                    <div className="font-semibold text-gray-900 mb-1">Cancellation Charges</div>
                    <div className="text-sm text-gray-600">
                      Cancellation charges are applied as per the hotel's cancellation policy at the time of booking.
                    </div>
                  </div>
                  
                  <div className="bg-white rounded p-3 border-l-4 border-red-500">
                    <div className="font-semibold text-gray-900 mb-1">Refund Method</div>
                    <div className="text-sm text-gray-600">
                      Refund will be credited to the original payment method used during booking.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Ticket Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Support Ticket</h3>
              <button
                onClick={() => setShowTicketModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleTicketSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation ID
                </label>
                <input
                  type="text"
                  value={cancellationId}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Query Type
                </label>
                <select
                  value={ticketForm.queryType}
                  onChange={(e) => setTicketForm({ ...ticketForm, queryType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="CANCELLATION">CANCELLATION</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Query
                </label>
                <textarea
                  value={ticketForm.query}
                  onChange={(e) => setTicketForm({ ...ticketForm, query: e.target.value })}
                  placeholder="Enter your query"
                  rows={4}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                >
                  Submit
                </button>
                <button
                  type="button"
                  onClick={() => setShowTicketModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancellationDetail;